/**
 * The visitor's answer to the cookie banner (TTAPI-47, GDPR / ePrivacy Art. 5(3)). Google Analytics and
 * the first-visit attribution stash are non-essential, so neither runs until the visitor accepts.
 * Strictly necessary storage — the sign-in session cookie and UI preferences — never waits on this.
 *
 * Kept in localStorage on the visitor's own device; nothing about the choice is sent anywhere.
 */

export type CookieConsentChoice = "granted" | "denied";

type StoredConsent = {
	choice: CookieConsentChoice;
	/** When the choice was made, so a future policy change can ask again. */
	at: string;
};

const CONSENT_KEY = "tt_cookie_consent";
const CHANGE_EVENT = "tt:cookie-consent-change";
const OPEN_EVENT = "tt:cookie-settings-open";

/** The stored choice, or null when the visitor has not answered (or storage is unavailable). */
export const readCookieConsent = (): CookieConsentChoice | null => {
	try {
		const raw = window.localStorage.getItem(CONSENT_KEY);
		if (!raw) {
			return null;
		}

		const parsed = JSON.parse(raw) as Partial<StoredConsent>;

		return parsed.choice === "granted" || parsed.choice === "denied" ? parsed.choice : null;
	} catch {
		return null;
	}
};

/** True only after an explicit Accept. */
export const hasAnalyticsConsent = (): boolean => readCookieConsent() === "granted";

/** Stores the choice and tells every listener (the GA loader, the banner) about it. */
export const writeCookieConsent = (choice: CookieConsentChoice): void => {
	try {
		const stored: StoredConsent = { choice, at: new Date().toISOString() };
		window.localStorage.setItem(CONSENT_KEY, JSON.stringify(stored));
	} catch {
		// Storage unavailable: the choice still applies for this page, it just is not remembered.
	}

	document.dispatchEvent(new CustomEvent<CookieConsentChoice>(CHANGE_EVENT, { detail: choice }));
};

/** Runs <paramref name="listener"/> whenever the choice changes; returns the unsubscribe. */
export const onCookieConsentChange = (listener: (choice: CookieConsentChoice) => void): (() => void) => {
	const handler = (event: Event): void => listener((event as CustomEvent<CookieConsentChoice>).detail);
	document.addEventListener(CHANGE_EVENT, handler);

	return () => document.removeEventListener(CHANGE_EVENT, handler);
};

/** Reopens the banner so the visitor can change their mind (the footer's "Cookie settings" link). */
export const openCookieSettings = (): void => {
	document.dispatchEvent(new Event(OPEN_EVENT));
};

/** Runs <paramref name="listener"/> when something asks for the cookie settings; returns the unsubscribe. */
export const onCookieSettingsOpen = (listener: () => void): (() => void) => {
	document.addEventListener(OPEN_EVENT, listener);

	return () => document.removeEventListener(OPEN_EVENT, listener);
};
